/**
 * DraftCalendar field-name constants
 *
 * Single source of truth for every string key used across:
 *   - DraftCalendarTable  (column sort/filter keys)
 *   - AddMeetingForm       (react-hook-form field names)
 *   - BulkEditFlyout       (local FormState keys  +  BulkUpdateDraftFieldsRequest keys)
 *   - MeetingFlyout        (sign-off / lock field references)
 *
 * Import the relevant group instead of using raw strings, so a backend rename
 * only needs to be updated here.
 */

import { Statuses } from "./statuses";

// ─── DraftCalendarTable column keys ──────────────────────────────────────────
// Matches the `key` property in the COLUMNS array of DraftCalendarTable.tsx

export const DC_TABLE_COL = {
  // Icon / indicator columns
  IS_READ:              "isRead",
  UNSEEN_COMMENTS:      "unSeenComments",
  SHOW_IN_GRC:          "showInGRC",

  // Status columns
  STATE:                "state",
  SUMMIT_MEETING_STATUS:"summitMeetingStatus",
  DU_MEETING_STATUS:    "duMeetingStatus",

  // Identity / tier
  STATUS:               "status",          // Tier (Core / Secondary)
  PRIOR_ID:             "priorId",
  MEETING_ID:           "meetingId",       // New Summit ID

  // Core meeting columns
  BUSINESS:             "business",
  MEETING_NAME:         "meetingName",
  ROOM_BLOCK_START:     "roomBlockStart",
  ROOM_BLOCK_END:       "roomBlockEnd",
  EVENT_START:          "eventStart",
  EVENT_END:            "eventEnd",
  START_TIME_STRING:    "startTimeString",
  END_TIME_STRING:      "endTimeString",
  NIGHT_GUEST_NUMBER:   "nightGuestNumber",
  ALL_IN_ATTENDEES:     "allInAttendees",
  MEETING_TYPE:         "meetingType",
  BUSINESS_LINE:        "businessLine",
  ATTENDEE_LEVEL:       "attendeeLevel",
  MEETING_CATEGORY:     "meetingCategory",

  // People columns
  PROGRAM_OWNER:        "summitProgramOwnerName",   // CLO or Sponsoring PPMD
  PROGRAM_CONTACT:      "summitProgramContact",
  MEETING_PLANNER:      "summitMeetingPlanner",     // Business Advisor
  PROGRAM_MANAGER:      "programManager",

  // Metadata
  LAST_UPDATED:         "lastUpdated",
  SIGN_OFF_STATUS:      "signOffStatus",
} as const;

export type DcTableColKey = (typeof DC_TABLE_COL)[keyof typeof DC_TABLE_COL];

// ─── AddMeetingForm field names (react-hook-form) ────────────────────────────
// Matches the Zod schema keys in AddMeetingForm.tsx.

export const ADD_MEETING_FIELD = {
  MEETING_NAME:           "meetingName",
  BUSINESS:               "business",
  BUSINESS_LINE:          "businessLine",
  MEETING_TYPE:           "meetingType",
  MEETING_CATEGORY:       "meetingCategory",
  STATE:                  "state",
  STATUS:                 "status",           // Tier
  SHOW_IN_GRC:            "showInGRC",
  LOCATION:               "location",
  ATTENDEE_LEVEL:         "attendeeLevel",
  PROGRAM_OWNER:          "programOwner",
  PROGRAM_OWNER_EMAIL:    "programOwnerEmail",
  PROGRAM_CONTACT:        "programContact",
  PROGRAM_CONTACT_EMAIL:  "programContactEmail",
  PRIMARY_CONTACT:        "primaryContact",
  PRIMARY_CONTACT_EMAIL:  "primaryContactEmail",
  BUSINESS_ADVISOR:       "businessAdvisor",
  BUSINESS_ADVISOR_EMAIL: "businessAdvisorEmail",
  PROGRAM_MANAGER:        "programManagerName",
  PROGRAM_MANAGER_EMAIL:  "programManagerEmail",
  SIGN_OFF_STATUS:        "signOffStatus",
  PRIOR_ID:               "priorId",
  DU_DECLINE_REASON:      "duDeclineReason",
  DU_DECLINE_COMMENTS:    "duDeclineComments",
  NEW_COMMENT:            "newComment",
  DRAFT_PREFERRED_DATES:  "draftPreferredDates",

  // Nested inside each preferred-date entry
  ROOM_BLOCK_START_DATE:  "roomBlockStartDate",
  ROOM_BLOCK_END_DATE:    "roomBlockEndDate",
  MEETING_START_DATE:     "meetingStartDate",
  MEETING_END_DATE:       "meetingEndDate",
  START_TIME:             "startTime",
  END_TIME:               "endTime",
  ALL_IN_ATTENDEES:       "allInAttendees",
} as const;

export type AddMeetingFieldKey = (typeof ADD_MEETING_FIELD)[keyof typeof ADD_MEETING_FIELD];

// ─── BulkEditFlyout — local FormState keys ───────────────────────────────────
// Matches the FormState interface in BulkEditFlyout.tsx.

export const BULK_EDIT_FIELD = {
  MEETING_NAME:           "meetingName",
  DRAFT_EVENT_STATE:      "draftEventState",
  SIGN_OFF_STATUS:        "signOffStatus",
  STATUS:                 "status",           // Tier
  SHOW_IN_GRC:            "showInGRC",
  START_TIME:             "startTime",
  END_TIME:               "endTime",
  ALL_IN_ATTENDEES:       "allInAttendees",
  PROGRAM_OWNER:          "programOwner",
  PROGRAM_OWNER_EMAIL:    "programOwnerEmail",
  PROGRAM_CONTACT:        "programContact",
  PROGRAM_CONTACT_EMAIL:  "programContactEmail",
  BUSINESS_ADVISOR:       "businessAdvisor",
  BUSINESS_ADVISOR_EMAIL: "businessAdvisorEmail",
  PROGRAM_MANAGER:        "programManager",
  PROGRAM_MANAGER_EMAIL:  "programManagerEmail",
  MEETING_CATEGORY:       "meetingCategory",
  MEETING_TYPE:           "meetingType",
  BUSINESS:               "business",
  BUSINESS_LINE:          "businessLine",
  ATTENDEE_LEVEL:         "attendeeLevel",
} as const;

export type BulkEditFieldKey = (typeof BULK_EDIT_FIELD)[keyof typeof BULK_EDIT_FIELD];

// ─── BulkUpdateDraftFieldsRequest → DraftCalendarTable column mapping ─────────
// Used in DraftCalendarPage.handleBulkFieldsSave to decide which table cells
// to highlight after a successful bulk edit.
// Keys are BulkUpdateDraftFieldsRequest field names (same as BULK_EDIT_FIELD values
// for the fields that are sent to the server).

export const BULK_REQUEST_TO_TABLE_COL: Record<string, DcTableColKey> = {
  [BULK_EDIT_FIELD.MEETING_NAME]:       DC_TABLE_COL.MEETING_NAME,
  [BULK_EDIT_FIELD.DRAFT_EVENT_STATE]:  DC_TABLE_COL.STATE,
  [BULK_EDIT_FIELD.SIGN_OFF_STATUS]:    DC_TABLE_COL.SIGN_OFF_STATUS,
  [BULK_EDIT_FIELD.STATUS]:             DC_TABLE_COL.STATUS,
  [BULK_EDIT_FIELD.SHOW_IN_GRC]:        DC_TABLE_COL.SHOW_IN_GRC,
  [BULK_EDIT_FIELD.START_TIME]:         DC_TABLE_COL.START_TIME_STRING,
  [BULK_EDIT_FIELD.END_TIME]:           DC_TABLE_COL.END_TIME_STRING,
  [BULK_EDIT_FIELD.ALL_IN_ATTENDEES]:   DC_TABLE_COL.ALL_IN_ATTENDEES,
  [BULK_EDIT_FIELD.PROGRAM_OWNER]:      DC_TABLE_COL.PROGRAM_OWNER,
  [BULK_EDIT_FIELD.PROGRAM_CONTACT]:    DC_TABLE_COL.PROGRAM_CONTACT,
  [BULK_EDIT_FIELD.BUSINESS_ADVISOR]:   DC_TABLE_COL.MEETING_PLANNER,
  [BULK_EDIT_FIELD.PROGRAM_MANAGER]:    DC_TABLE_COL.PROGRAM_MANAGER,
  [BULK_EDIT_FIELD.BUSINESS]:           DC_TABLE_COL.BUSINESS,
  [BULK_EDIT_FIELD.BUSINESS_LINE]:      DC_TABLE_COL.BUSINESS_LINE,
  [BULK_EDIT_FIELD.MEETING_TYPE]:       DC_TABLE_COL.MEETING_TYPE,
  [BULK_EDIT_FIELD.MEETING_CATEGORY]:   DC_TABLE_COL.MEETING_CATEGORY,
  [BULK_EDIT_FIELD.ATTENDEE_LEVEL]:     DC_TABLE_COL.ATTENDEE_LEVEL,
};

// ─── Shared labels across Draft calendar surfaces ───────────────────────────
// These values are consumed by DraftCalendarTable, AddMeetingForm,
// BulkEditFlyout, and MeetingFlyout to avoid duplicated text literals.

export const DC_COMMON_LABEL = {
  ADD_MEETING: "Add Meeting",
  EDIT: "Edit",
  EDIT_MEETING: "Edit Meeting",
  MEETING_DETAILS: "Meeting Details",
  HISTORY: "History",
  CLOSE: "Close",

  DRAFT_MEETING_STATUS: "Draft Meeting Status",
  SIGN_OFF_STATUS: "Sign Off Status",
  SIGN_OFF: "Sign Off",
  SIGN_OFF_STATUS_HYPHEN: "Sign-Off Status",
  TIER: "Tier",
  PRIOR_ID: "Prior ID",
  NEW_ID: "New ID",
  SHOW_IN_GRC: "Show In GRC",

  MEETING_NAME: "Meeting Name",
  BUSINESS: "Business",
  BUSINESS_LINE: "Business Line",
  MEETING_TYPE: "Meeting Type",
  MEETING_CATEGORY: "Meeting Category",
  ATTENDEE_LEVEL: "Attendee Level",
  LOCATION: "Location",

  ROOM_BLOCK_START: "Room Block Start",
  ROOM_BLOCK_END: "Room Block End",
  MEETING_START: "Meeting Start",
  MEETING_END: "Meeting End",
  START_TIME: "Start Time",
  END_TIME: "End Time",
  NIGHT_GUESTS: "Night Guests",
  ALL_IN_ATTENDEES: "All In Attendees",

  PROGRAM_OWNER: "CLO or Sponsoring PPMD",
  PROGRAM_CONTACT: "Program Contact",
  BUSINESS_ADVISOR: "Business Advisor",
  PROGRAM_MANAGER: "Program Manager",

  ROOM_BLOCK_START_DATE: "Room Block Start Date",
  ROOM_BLOCK_END_DATE: "Room Block End Date",
  MEETING_START_DATE: "Meeting Start Date",
  MEETING_END_DATE: "Meeting End Date",
  MEETING_START_TIME: "Meeting Start Time",
  MEETING_END_TIME: "Meeting End Time",
} as const;

export const DC_COMMON_TEXT = {
  BULK_EDIT_HELP_TEXT: "Edit any of the inputs below, then click \"Save\" to apply it to all of the selected meetings.",
  BULK_EDIT_DATES_TIMES_TITLE: "Times",
  BULK_EDIT_DATES_TIMES_HELP_TEXT: "Time changes will apply only to meeting marked \"Preferred\"",
  BULK_EDIT_ADMIN: "Admin",
  FLYOUT_HISTORY_LOADING: "Loading history...",
  FLYOUT_HISTORY_EMPTY: "No history available.",
  FLYOUT_SIGNED_OFF_HELP_TEXT: "To request a change, reach out to your MES Business Advisor.",

  REJECT_REASON_PLACEHOLDER: "-- Select reason --",
  REJECT_REASON_OPTIONS: [
    "Incorrect Information",
    "Change Request",
    "No Longer Needed",
    "Other",
  ] as const,

  DATES_PREFERRED_NOTE: "preferred date will show in draft calendar",
  COMMENT_PLACEHOLDER: "Add a comment...",
  PUSH_TO_SUMMIT_CONFIRM: "Are you sure you want to push this meeting to Summit?",
} as const;

export const DRAFT_MEETING_STATUS_OPTIONS = {
  DEFAULT: [
    "Program Owner Reviewed",
    "DU Review",
    "Scheduled",
    "Push to Summit",
    "DU Declined",
    "Removed",
  ] as const,
  APPROVER_AND_REVIEWER: [
    "Program Owner Reviewed",
    "Removed",
  ] as const,
} as const;

export function getDraftMeetingStatusOptions(isBusinessApprover = false, isBusinessReviewer = false) {
  return (isBusinessApprover || isBusinessReviewer)
    ? [...DRAFT_MEETING_STATUS_OPTIONS.APPROVER_AND_REVIEWER]
    : [...DRAFT_MEETING_STATUS_OPTIONS.DEFAULT];
}

/** Maps DB status values to user-facing display labels based on role. Admin/Scheduler see raw values; others see "Scheduled". */
export function getStatusDisplayLabel(status: string, isAdminOrScheduler: boolean): string {
  if (!isAdminOrScheduler && (status === Statuses.Resolved || status === Statuses.CreatedInSummit)) {
    return "Scheduled";
  }
  return status;
}

// ─── DraftCalendarTable labels ──────────────────────────────────────────────

export const DC_TABLE_LABEL = {
  IS_READ: "Read",
  UNSEEN_COMMENTS: "Comments",
  SHOW_IN_GRC: "GRC",
  STATE: DC_COMMON_LABEL.DRAFT_MEETING_STATUS,
  SUMMIT_MEETING_STATUS: "Summit Meeting Status",
  DU_MEETING_STATUS: "DU Status",
  STATUS: DC_COMMON_LABEL.TIER,
  PRIOR_ID: DC_COMMON_LABEL.PRIOR_ID,
  MEETING_ID: DC_COMMON_LABEL.NEW_ID,
  BUSINESS: DC_COMMON_LABEL.BUSINESS,
  MEETING_NAME: DC_COMMON_LABEL.MEETING_NAME,
  ROOM_BLOCK_START: DC_COMMON_LABEL.ROOM_BLOCK_START,
  ROOM_BLOCK_END: DC_COMMON_LABEL.ROOM_BLOCK_END,
  EVENT_START: DC_COMMON_LABEL.MEETING_START,
  EVENT_END: DC_COMMON_LABEL.MEETING_END,
  START_TIME_STRING: DC_COMMON_LABEL.START_TIME,
  END_TIME_STRING: DC_COMMON_LABEL.END_TIME,
  NIGHT_GUEST_NUMBER: DC_COMMON_LABEL.NIGHT_GUESTS,
  ALL_IN_ATTENDEES: DC_COMMON_LABEL.ALL_IN_ATTENDEES,
  MEETING_TYPE: DC_COMMON_LABEL.MEETING_TYPE,
  BUSINESS_LINE: DC_COMMON_LABEL.BUSINESS_LINE,
  ATTENDEE_LEVEL: DC_COMMON_LABEL.ATTENDEE_LEVEL,
  MEETING_CATEGORY: DC_COMMON_LABEL.MEETING_CATEGORY,
  PROGRAM_OWNER: DC_COMMON_LABEL.PROGRAM_OWNER,
  PROGRAM_CONTACT: DC_COMMON_LABEL.PROGRAM_CONTACT,
  MEETING_PLANNER: DC_COMMON_LABEL.BUSINESS_ADVISOR,
  PROGRAM_MANAGER: DC_COMMON_LABEL.PROGRAM_MANAGER,
  LAST_UPDATED: "Last Updated",
  SIGN_OFF_STATUS: DC_COMMON_LABEL.SIGN_OFF_STATUS,
} as const;

// ─── AddMeetingForm labels + ids ────────────────────────────────────────────

export const ADD_MEETING_LABEL = {
  SIGN_OFF_STATUS: DC_COMMON_LABEL.SIGN_OFF_STATUS_HYPHEN,
  PRIOR_ID: DC_COMMON_LABEL.PRIOR_ID,
  NEW_ID: DC_COMMON_LABEL.NEW_ID,
  DRAFT_MEETING_STATUS: DC_COMMON_LABEL.DRAFT_MEETING_STATUS,
  REASON_FOR_DU_DECLINE: "Reason for DU Decline",
  SHOW_IN_GRC: DC_COMMON_LABEL.SHOW_IN_GRC,
  DU_DECLINE_COMMENT: "DU Decline Comment",
  TIER: DC_COMMON_LABEL.TIER,
  MEETING_NAME: DC_COMMON_LABEL.MEETING_NAME,
  BUSINESS: DC_COMMON_LABEL.BUSINESS,
  BUSINESS_LINE: DC_COMMON_LABEL.BUSINESS_LINE,
  MEETING_TYPE: DC_COMMON_LABEL.MEETING_TYPE,
  MEETING_CATEGORY: DC_COMMON_LABEL.MEETING_CATEGORY,
  ATTENDEE_LEVEL: DC_COMMON_LABEL.ATTENDEE_LEVEL,
  LOCATION: DC_COMMON_LABEL.LOCATION,
  PROGRAM_OWNER: DC_COMMON_LABEL.PROGRAM_OWNER,
  PROGRAM_CONTACT: DC_COMMON_LABEL.PROGRAM_CONTACT,
  PRIMARY_CONTACT: "Primary Contact",
  BUSINESS_ADVISOR: DC_COMMON_LABEL.BUSINESS_ADVISOR,
  PROGRAM_MANAGER: DC_COMMON_LABEL.PROGRAM_MANAGER,
  RESPONSIBLE_FOR_SIGN_OFF: "Responsible for Sign-Off",
  DATES_AND_ROOM_BLOCKS: "Dates & Room Blocks",
  NOTES: "Notes",
  ROOM_BLOCK_START_DATE: DC_COMMON_LABEL.ROOM_BLOCK_START_DATE,
  ROOM_BLOCK_END_DATE: DC_COMMON_LABEL.ROOM_BLOCK_END_DATE,
  MEETING_START_DATE: DC_COMMON_LABEL.MEETING_START_DATE,
  MEETING_START_TIME: DC_COMMON_LABEL.MEETING_START_TIME,
  MEETING_END_DATE: DC_COMMON_LABEL.MEETING_END_DATE,
  MEETING_END_TIME: DC_COMMON_LABEL.MEETING_END_TIME,
  ALL_IN_ATTENDEES: DC_COMMON_LABEL.ALL_IN_ATTENDEES,
} as const;

export const ADD_MEETING_ID = {
  FORM: "meeting-form",
  SIGNOFF_STATUS: "signoff-status",
  PRIOR_ID: "prior-id",
  NEW_ID: "new-id",
  MEETING_STATE: "meeting-state",
  DU_DECLINE_REASON: "du-decline-reason",
  SHOW_IN_GRC_INLINE: "show-in-grc-inline",
  DU_DECLINE_COMMENTS: "du-decline-comments",
  SHOW_IN_GRC: "show-in-grc",
  MEETING_TIER: "meeting-tier",
  MEETING_NAME: "meeting-name",
  BUSINESS: "business",
  BUSINESS_LINE: "business-line",
  MEETING_TYPE: "meeting-type",
  MEETING_CATEGORY: "meeting-category",
  ATTENDEE_LEVEL: "attendee-level",
  LOCATION: "location",
  PROGRAM_OWNER: "program-owner",
  PROGRAM_CONTACT: "program-contact",
  PRIMARY_CONTACT: "primary-contact",
  BUSINESS_ADVISOR: "business-advisor",
  PROGRAM_MANAGER: "program-manager",
  SIGN_OFF_USERS: "sign-off-users",
  SIGN_OFF_USERS_READONLY: "sign-off-users-readonly",
  DATES_HEADING: "dates-heading",
  NOTES_HEADING: "notes-heading",
  NEW_COMMENT: "new-comment",
  RB_START_PREFIX: "rb-start",
  RB_END_PREFIX: "rb-end",
  MTG_START_PREFIX: "mtg-start",
  START_TIME_PREFIX: "start-time",
  MTG_END_PREFIX: "mtg-end",
  END_TIME_PREFIX: "end-time",
} as const;

// ─── BulkEditFlyout labels + ids ────────────────────────────────────────────

export const BULK_EDIT_LABEL = {
  DIALOG_TITLE_PREFIX: DC_COMMON_LABEL.EDIT,
  CLOSE: DC_COMMON_LABEL.CLOSE,
  DATES_TIMES_TITLE: DC_COMMON_TEXT.BULK_EDIT_DATES_TIMES_TITLE,
  ADMIN: DC_COMMON_TEXT.BULK_EDIT_ADMIN,
  DATES_TIMES_HELP_TEXT: DC_COMMON_TEXT.BULK_EDIT_DATES_TIMES_HELP_TEXT,
  MEETING_NAME: DC_COMMON_LABEL.MEETING_NAME,
  MEETING_START_TIME: DC_COMMON_LABEL.MEETING_START_TIME,
  MEETING_END_TIME: DC_COMMON_LABEL.MEETING_END_TIME,
  ALL_IN_ATTENDEES: DC_COMMON_LABEL.ALL_IN_ATTENDEES,
  PROGRAM_OWNER: DC_COMMON_LABEL.PROGRAM_OWNER,
  PROGRAM_CONTACT: DC_COMMON_LABEL.PROGRAM_CONTACT,
  BUSINESS_ADVISOR: DC_COMMON_LABEL.BUSINESS_ADVISOR,
  PROGRAM_MANAGER: DC_COMMON_LABEL.PROGRAM_MANAGER,
  DRAFT_MEETING_STATUS: DC_COMMON_LABEL.DRAFT_MEETING_STATUS,
  SIGN_OFF_STATUS: DC_COMMON_LABEL.SIGN_OFF_STATUS,
  TIER: DC_COMMON_LABEL.TIER,
  SHOW_IN_GRC: DC_COMMON_LABEL.SHOW_IN_GRC,
  BUSINESS: DC_COMMON_LABEL.BUSINESS,
  BUSINESS_LINE: DC_COMMON_LABEL.BUSINESS_LINE,
  MEETING_TYPE: DC_COMMON_LABEL.MEETING_TYPE,
  MEETING_CATEGORY: DC_COMMON_LABEL.MEETING_CATEGORY,
  ATTENDEE_LEVEL: DC_COMMON_LABEL.ATTENDEE_LEVEL,
} as const;

export const BULK_EDIT_ID = {
  DIALOG_TITLE: "bulk-edit-title",
  START_TIME: "meeting-start-time",
  END_TIME: "meeting-end-time",
  PROGRAM_OWNER: "bulk-program-owner",
  PROGRAM_CONTACT: "bulk-program-contact",
  BUSINESS_ADVISOR: "bulk-business-advisor",
  PROGRAM_MANAGER: "bulk-program-manager",
} as const;

// ─── BulkAddCommentDialog labels + ids ──────────────────────────────────────

export const BULK_ADD_COMMENT_LABEL = {
  DIALOG_TITLE_PREFIX: "Add",
  CLOSE: DC_COMMON_LABEL.CLOSE,
  COMMENT: "Comment",
  SAVE: "Save",
  CANCEL: "Cancel",
} as const;

export const BULK_ADD_COMMENT_ID = {
  DIALOG_TITLE: "bulk-add-comment-title",
  COMMENT_INPUT: "bulk-add-comment-input",
} as const;

// ─── MeetingFlyout labels + ids ─────────────────────────────────────────────

export const MEETING_FLYOUT_LABEL = {
  TITLE_ADD: DC_COMMON_LABEL.ADD_MEETING,
  TITLE_DETAILS: DC_COMMON_LABEL.MEETING_DETAILS,
  TITLE_EDIT: DC_COMMON_LABEL.EDIT_MEETING,
  TAB_MEETING_DETAILS: DC_COMMON_LABEL.MEETING_DETAILS,
  TAB_HISTORY: DC_COMMON_LABEL.HISTORY,
  TABLIST_LABEL: "Meeting sections",
  HISTORY_UNAVAILABLE_WHILE_EDITING: "History is unavailable while editing.",
  HISTORY_PANEL_LABEL: "Meeting history",
  REJECT_SIGN_OFF_TITLE: "Reject Sign Off",
  REJECT_REASON_LABEL: "Please select the reason for rejection",
  SIGN_OFF_BUTTON: DC_COMMON_LABEL.SIGN_OFF,
  REJECT_SIGN_OFF_BUTTON: "Reject Sign Off",
} as const;

export const MEETING_FLYOUT_ID = {
  DIALOG_TITLE: "meeting-flyout-title",
  REJECT_SIGNOFF_TITLE: "reject-signoff-title",
  REJECT_REASON: "reject-reason",
} as const;
